// --- Dependencies -------------------------------------------------------------------------------
import { useCallback, useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import useTranslation from "@/hooks/useTranslation";
import type { TyFile } from "@/types";
import { formatBytes, resolvePreviewKind } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyFilePreviewDetails = {
    /** The whole list, so the arrows can step through a folder without reopening the viewer. */
    files: TyFile[];
    index: number;
};

type Props = { onClose: () => void; details?: unknown };

/** The viewer needs a URL before it can ask for one through a hook, and it re-asks on every step. */
async function fetchLink(fileId: string, token: string | null, organizationId: string | null, lang: string, isDownload = false): Promise<string> {
    const query = isDownload ? "download=1" : "preview=1";
    const response = await fetch(`${constants.url}/drive/${fileId}/link?${query}`, {
        headers: {
            "x-language": lang,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(organizationId ? { "x-organization-id": organizationId } : {}),
        },
    });
    const payload = await response.json();
    if (!response.ok || !payload.success) throw new Error(payload.message);
    return payload.data.url as string;
}

/**
 * Opens a file in place instead of throwing it at a new browser tab, and steps through the rest of the
 * folder with the arrows — looking at a gallery one photo at a time is most of what a viewer is for.
 *
 * It renders only what the browser renders natively: images, video, PDF and plain text. Everything
 * else says so and offers the download, which is a better answer than an empty frame. The gap is in
 * `utils/preview.ts`, with the reason for each exclusion.
 *
 * The URL is signed for four hours rather than the usual fifteen minutes, because a viewer is left
 * open far longer than a download is clicked.
 */
export default function FilePreviewPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const token = useAuth((state) => state.token);
    const organizationId = useOrganization((state) => state.organizationId);
    const { files, index: opened } = details as TyFilePreviewDetails;

    const [index, setIndex] = useState(opened);
    /**
     * One object keyed by the file it belongs to, rather than three fields cleared on every step. The
     * key is what makes a half-loaded step show the loader instead of the previous file's picture, and
     * it keeps the effect from setting state synchronously just to wipe it.
     */
    const [loaded, setLoaded] = useState<{ fileId: string; url: string | null; text: string | null; error: string | null } | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const file = files[index];
    const kind = file ? resolvePreviewKind(file.originalName || file.name) : null;
    const isReady = !!file && loaded?.fileId === file._id;
    const error = (isReady ? loaded?.error : null) ?? actionError;

    const step = useCallback((by: number) => setIndex((current) => Math.min(files.length - 1, Math.max(0, current + by))), [files.length]);

    // Escape closes, arrows walk. The direction decides which arrow means "forward".
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const isRtl = constants.rtlLanguages.includes(lang as "ar");
            if (event.key === "Escape") onClose();
            if (event.key === "ArrowRight") step(isRtl ? -1 : 1);
            if (event.key === "ArrowLeft") step(isRtl ? 1 : -1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, step, lang]);

    // One signed URL per file, asked for as it is reached rather than all of them up front.
    useEffect(() => {
        if (!file || !kind) return;
        let isCurrent = true;

        fetchLink(file._id, token, organizationId, lang)
            .then(async (link) => {
                // Text is the one kind the browser will not lay out for us.
                const body = kind === "text" ? await (await fetch(link)).text() : null;
                if (isCurrent) setLoaded({ fileId: file._id, url: link, text: body, error: null });
            })
            .catch((err) => isCurrent && setLoaded({ fileId: file._id, url: null, text: null, error: (err as Error).message }));

        return () => {
            isCurrent = false;
        };
    }, [file, kind, token, organizationId, lang]);

    const download = async () => {
        setActionError(null);
        try {
            window.open(await fetchLink(file._id, token, organizationId, lang, true), "_blank", "noopener");
        } catch (err) {
            setActionError((err as Error).message);
        }
    };

    if (!file) return null;

    return (
        <section className="file-preview">
            <header className="file-preview__head">
                <div className="file-preview__identity">
                    <span className="file-preview__name">{file.name}</span>
                    <span className="file-preview__meta">
                        {formatBytes(file.size, lang)}
                        {files.length > 1 && ` · ${index + 1}/${files.length}`}
                    </span>
                </div>

                <button type="button" className="file-preview__action" onClick={download} title={t.drive.actions.download}>
                    <Icon name="download" size={18} />
                </button>
                <button type="button" className="file-preview__action" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="file-preview__stage">
                {files.length > 1 && (
                    <button type="button" className="file-preview__step file-preview__step--prev" onClick={() => step(-1)} disabled={index === 0}>
                        <Icon name="chevron" size={18} />
                    </button>
                )}

                {/* Its own column between the arrows, with a definite height — which is what lets the
                    media size itself against the box instead of overflowing it. */}
                <div className="file-preview__viewport">
                    {error && <p className="file-preview__state file-preview__state--error">{error}</p>}

                    {!error && !kind && (
                        <div className="file-preview__state">
                            <Icon name="document" size={40} />
                            <p>{t.drive.noPreview}</p>
                            <button type="button" className="file-preview__download" onClick={download}>
                                {t.drive.actions.download}
                            </button>
                        </div>
                    )}

                    {!error && kind && !isReady && <p className="file-preview__state">{t.common.loading}</p>}

                    {!error && isReady && loaded!.url && kind === "image" && (
                        <img className="file-preview__image" src={loaded!.url!} alt={file.name} />
                    )}
                    {!error && isReady && loaded!.url && kind === "video" && (
                        <video className="file-preview__video" src={loaded!.url!} controls autoPlay />
                    )}
                    {!error && isReady && loaded!.url && kind === "pdf" && (
                        <iframe className="file-preview__frame" src={loaded!.url!} title={file.name} />
                    )}
                    {!error && isReady && loaded!.url && kind === "text" && <pre className="file-preview__text">{loaded!.text}</pre>}
                </div>

                {files.length > 1 && (
                    <button
                        type="button"
                        className="file-preview__step file-preview__step--next"
                        onClick={() => step(1)}
                        disabled={index === files.length - 1}
                    >
                        <Icon name="chevron" size={18} />
                    </button>
                )}
            </div>
        </section>
    );
}
